import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/TradeAuthContext";
import { Button } from "@/components/ui/button";
import {
  X, Check, ClipboardList, Copy, MessageCircle, Package,
  Landmark, CreditCard, FileText, Ship, Truck, BadgeCheck,
  ShieldCheck, Send, Warehouse, Calculator, ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const PROCESS_STEPS = [
  {
    id: "confirm_import_legality",
    stage: "Before You Buy",
    title: "Confirm the product can be imported",
    responsible: "Ethiopian importer",
    icon: ShieldCheck,
    description: "Identify the exact goods and check the HS code, customs procedure, and documents. Trailers, excavators, vehicles, electronics, tires, and other goods may need special permits, technical approvals, or certificates.",
    documents: ["HS code", "Commodity procedure", "Special permits if required", "Product specifications", "Conformity certificates"],
    links: [
      { label: "Ethiopian Customs Trade Portal", url: "https://customs.erca.gov.et/" },
      { label: "Ethiopia Business Portal", url: "https://new.business.gov.et/" }
    ]
  },
  {
    id: "prepare_import_documents",
    stage: "Before You Buy",
    title: "Prepare Ethiopian import documents",
    responsible: "Ethiopian importer + bank/customs agent",
    icon: Landmark,
    description: "Before paying, confirm the lawful foreign-currency payment route and the required import authorization. Using your own U.S. dollars does not remove Ethiopian import, bank, or customs requirements.",
    documents: ["Business registration/import license", "TIN", "Import permit or authorization", "Foreign-currency payment confirmation", "Product permits", "Ethiopian clearing agent", "Djibouti transit arrangement"]
  },
  {
    id: "obtain_quotation",
    stage: "Before You Buy",
    title: "Get a complete supplier quotation",
    responsible: "Chinese supplier",
    icon: Package,
    description: "Request a pro forma invoice before paying. Check seller and buyer details, product, model, quantity, price, origin, packing, weights, currency, payment terms, Incoterms, and dates. For machinery include serial, engine, load and technical details.",
    documents: ["Pro forma invoice", "Seller/buyer company details", "Model and specifications", "Quantity and price", "HS code", "Incoterms", "Production/shipping dates"]
  },
  {
    id: "pay_supplier",
    stage: "Purchase and Shipping",
    title: "Pay supplier and keep evidence",
    responsible: "Ethiopian importer + Chinese supplier",
    icon: CreditCard,
    description: "Approve the contract or pro forma invoice, pay through the lawful channel, save the transfer receipt and supplier confirmation, and request the final commercial invoice. Arrange pre-shipment inspection for expensive or specification-critical goods. Never understate invoice value.",
    documents: ["Purchase contract/approved PI", "Payment receipt", "Supplier payment confirmation", "Final commercial invoice", "Pre-shipment inspection report"]
  },
  {
    id: "collect_shipping_documents",
    stage: "Purchase and Shipping",
    title: "Collect the shipping documents",
    responsible: "Chinese supplier + freight forwarder",
    icon: FileText,
    description: "Coordinate the required shipping package before the vessel departs: commercial invoice, packing list, Bill of Lading, Certificate of Origin, and any insurance, inspection, product, payment, or import documents.",
    documents: ["Commercial invoice", "Packing list", "Bill of Lading", "Certificate of Origin", "Insurance certificate", "Inspection/product certificates", "Payment/import authorization"]
  },
  {
    id: "check_bl_release",
    stage: "Purchase and Shipping",
    title: "Check B/L and release method",
    responsible: "Carrier + supplier + forwarder",
    icon: Ship,
    description: "Review the draft Bill of Lading before final issue. Check consignee, notify party, cargo description, package count, ports, and transit route. Confirm whether release will be original B/L, sea waybill, or telex release. Telex release does not replace customs or import documents.",
    documents: ["Draft B/L", "Final B/L", "Correct consignee/notify party", "Package and weight match", "Release method confirmation"]
  },
  {
    id: "send_docs_forwarders",
    stage: "Before Arrival",
    title: "Send documents to both forwarders",
    responsible: "Ethiopian importer",
    icon: Send,
    description: "Do not wait for the ship to arrive. Send scanned copies to the Ethiopian and Djiboutian forwarders as soon as draft or final documents are available, and ask the Ethiopian forwarder to confirm the package is complete in writing.",
    documents: ["Commercial invoice", "Packing list", "Draft/final B/L", "Certificate of Origin", "Import/payment authorization", "Insurance", "Inspection/product certificates", "Clearing authorization"]
  },
  {
    id: "djibouti_port_transit",
    stage: "Djibouti Transit",
    title: "Complete Djibouti port and transit process",
    responsible: "Djibouti clearing agent + Ethiopian forwarder",
    icon: Truck,
    description: "After arrival, obtain arrival/charge information, satisfy shipping-line release requirements, get the Delivery Order, complete transit clearance, pay port/handling charges, and arrange truck loading. Cargo must be declared for transit to Ethiopia, not local Djibouti consumption.",
    documents: ["Arrival notice/charges", "Delivery Order", "Djibouti transit clearance", "Port/handling payment", "Truck loading confirmation", "Transit-to-Ethiopia declaration"]
  },
  {
    id: "truck_border_documents",
    stage: "Road to Ethiopia",
    title: "Prepare truck and border documents",
    responsible: "Road transporter + Djibouti agent",
    icon: Truck,
    description: "Before the cargo leaves port, collect transit declaration, truck waybill, truck and driver identification, seal number, port exit/dispatch documents, road permits, and insurance. Confirm the authorized route and border point, such as Galafi, and do not break customs seals without authorization.",
    documents: ["Djibouti transit declaration/T1", "Truck waybill", "Truck and driver ID", "Container/cargo seal number", "Port exit document", "Road permits", "Insurance"]
  },
  {
    id: "ethiopia_border_customs",
    stage: "Ethiopian Customs",
    title: "Complete border and customs procedures",
    responsible: "Ethiopian importer + customs broker",
    icon: BadgeCheck,
    description: "At the Ethiopian border, present transit and cargo documents, allow seal/cargo checks, move to the designated customs office or dry port, complete import declaration and valuation, pay assessed duties and taxes, and obtain customs release.",
    documents: ["Transit/cargo documents", "Seal inspection", "Final import declaration", "Valuation documents", "Duty/tax payment", "Inspection result", "Customs release"]
  },
  {
    id: "receive_goods",
    stage: "Receive Goods",
    title: "Collect cargo and close delivery",
    responsible: "Ethiopian importer + warehouse/transporter",
    icon: Warehouse,
    description: "After customs release, arrange delivery, inspect for damage or missing items, match quantity to the packing list, keep declarations and receipts, and return any carrier-owned container within free time to avoid detention charges.",
    documents: ["Release order", "Delivery note", "Damage/shortage record", "Packing-list quantity check", "Customs declaration", "Duty/tax receipts", "Container return confirmation"]
  },
  {
    id: "calculate_landed_cost",
    stage: "Close Shipment",
    title: "Calculate actual landed cost",
    responsible: "Ethiopian importer",
    icon: Calculator,
    description: "Landed cost per unit = total shipment cost ÷ saleable units received. Use actual quotations and customs assessment, not only the China purchase price.",
    documents: ["Goods/supplier charges", "China inland/export/inspection", "Ocean freight to Djibouti", "Cargo insurance", "Djibouti port/transit", "Road transport/waybill", "Ethiopian duties/taxes/clearance", "Storage/demurrage/detention/local delivery"]
  }
];

const OLD_STEP_MAP = {
  purchase_goods: "obtain_quotation",
  import_authorization: "prepare_import_documents",
  payment_shipping: "pay_supplier",
  shipping_documents: "collect_shipping_documents",
  telex_release_documents: "check_bl_release",
  djibouti_transit: "djibouti_port_transit",
  ethiopia_customs: "ethiopia_border_customs"
};

const SUPPLIER_MESSAGE = `Dear Supplier,

Please follow these steps for our shipment from China to Djibouti, with final delivery to Ethiopia.

Step 1 — Before payment
Send me the pro forma invoice showing the goods, quantity, unit price, total price in USD, HS code, and shipping terms.

Step 2 — After purchase and shipment booking
Confirm the shipping booking and provide the draft Bill of Lading for my review before final issuance.

Step 3 — Final shipping documents
After shipment, please provide:
- Final commercial invoice
- Final packing list showing quantity, gross weight, net weight, and package dimensions
- Certificate of Origin
- Final Bill of Lading
- Telex release confirmation, if applicable
- China export customs declaration or export clearance document, if available or required
- Cargo insurance certificate, if applicable
- Any other documents required for Djibouti transit and Ethiopian customs clearance

Step 4 — Transport to Ethiopia
Please coordinate with the shipping company or freight forwarder to confirm the documents required for cargo release in Djibouti and onward transit to Ethiopia. The Djibouti clearing agent or transporter will arrange the applicable transit declaration and truck waybill.

Step 5 — Import permit
Please provide any supplier or shipping documents required for my Ethiopian import authorization, including Franco-Valuta processing if applicable. The Ethiopian permit must be obtained through the relevant authorized process; it is not issued by the Chinese supplier.

Please ensure the supplier name, consignee details, invoice value, cargo description, and shipment details are consistent across all documents. Send me scanned copies as soon as they are available.

Thank you.`;

const ShippingProcessModal = ({ shipment = null, onClose, onProgress }) => {
  const { API, authHeader } = useAuth();
  const normalizeProgress = (raw = {}) => {
    const next = { ...raw };
    Object.entries(OLD_STEP_MAP).forEach(([oldId, newId]) => {
      if (raw[oldId]?.done && !next[newId]?.done) next[newId] = raw[oldId];
    });
    return next;
  };
  const [progress, setProgress] = useState(normalizeProgress(shipment?.process_checklist || {}));
  const [savingStep, setSavingStep] = useState(null);
  const isChecklist = !!shipment;
  const doneCount = PROCESS_STEPS.filter(step => progress[step.id]?.done).length;

  const toggleStep = async (step) => {
    if (!isChecklist || savingStep) return;
    const nextDone = !progress[step.id]?.done;
    setSavingStep(step.id);
    try {
      const res = await axios.patch(
        `${API}/trade/shipments/${shipment.id}/process-checklist/${step.id}`,
        { done: nextDone },
        { headers: authHeader() }
      );
      const normalized = normalizeProgress(res.data.process_checklist || {});
      setProgress(normalized);
      if (onProgress) onProgress(shipment.id, normalized);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update checklist");
    } finally {
      setSavingStep(null);
    }
  };

  const copySupplierMessage = async () => {
    try {
      await navigator.clipboard.writeText(SUPPLIER_MESSAGE);
      toast.success("Supplier message copied");
    } catch {
      toast.error("Could not copy message");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="shipping-process-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card rounded-sm p-6 w-full max-w-5xl max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-5 h-5 trade-gold" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] trade-gold">China → Djibouti → Ethiopia</p>
              <h2 className="text-2xl font-serif font-bold" data-testid="process-modal-title">
                {isChecklist ? `Checklist — ${shipment.shipment_name}` : "Shipping Process Guide"}
              </h2>
              <p className="text-xs text-white/45 mt-1">12 steps from supplier quotation to landed cost in Ethiopia</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white" data-testid="process-modal-close">
            <X size={20} />
          </button>
        </div>

        {isChecklist && (
          <div className="mb-6 p-4 bg-white/5 rounded-sm" data-testid="process-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest text-white/40">Progress</span>
              <span className="text-sm trade-gold font-semibold">{doneCount}/{PROCESS_STEPS.length} done</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#D4AF37] transition-all"
                style={{ width: `${(doneCount / PROCESS_STEPS.length) * 100}%` }}
                data-testid="process-progress-bar"
              />
            </div>
          </div>
        )}

        <div className="space-y-4 mb-8">
          {PROCESS_STEPS.map((step, index) => {
            const done = !!progress[step.id]?.done;
            const Icon = step.icon;
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => toggleStep(step)}
                disabled={!isChecklist || savingStep === step.id}
                className={`w-full text-left p-4 rounded-sm border transition-all ${
                  done
                    ? "border-[#10b981]/30 bg-[#10b981]/5"
                    : "border-white/10 bg-white/5 hover:border-[#D4AF37]/30"
                } ${!isChecklist ? "cursor-default" : ""}`}
                data-testid={`process-step-${step.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${
                    done ? "bg-[#10b981] text-black" : "bg-[#D4AF37]/10 trade-gold"
                  }`}>
                    {done ? <Check size={17} /> : <Icon size={17} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-white/35 mb-1">{step.stage} · Step {index + 1}</p>
                        <h3 className="font-serif font-semibold text-lg">{step.title}</h3>
                      </div>
                      {isChecklist && (
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${
                          done ? "bg-[#10b981]/10 trade-green" : "bg-white/5 text-white/40"
                        }`}>
                          {savingStep === step.id ? "Saving..." : done ? "Done" : "Pending"}
                        </span>
                      )}
                    </div>
                    <p className="text-xs trade-gold mt-1 mb-2">Responsible: {step.responsible}</p>
                    <p className="text-sm text-white/65 leading-relaxed mb-3">{step.description}</p>
                    {step.links && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {step.links.map(link => (
                          <a
                            key={link.url}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                            data-testid={`process-link-${step.id}-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            <ExternalLink size={11} /> {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {step.documents.map(doc => (
                        <span key={doc} className="px-2 py-1 text-[10px] rounded-sm bg-white/5 text-white/55 border border-white/5">
                          {doc}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="p-5 rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5" data-testid="permit-distinction-card">
            <h3 className="font-serif font-semibold text-lg mb-3">Do Not Confuse These</h3>
            <div className="space-y-3 text-sm text-white/65">
              <p><span className="trade-gold font-semibold">Import authorization:</span> Ethiopian import procedure, not a shipping document.</p>
              <p><span className="trade-gold font-semibold">Pro forma invoice:</span> preliminary quotation before payment.</p>
              <p><span className="trade-gold font-semibold">Commercial invoice:</span> records the actual sale.</p>
              <p><span className="trade-gold font-semibold">Bill of Lading:</span> issued by the carrier.</p>
              <p><span className="trade-gold font-semibold">Telex release:</span> a release instruction, not another Bill of Lading and not an import permit.</p>
              <p><span className="trade-gold font-semibold">Truck waybill:</span> normally arranged by the road carrier for inland movement.</p>
              <p><span className="trade-gold font-semibold">Transit cargo:</span> moving through Djibouti to Ethiopia, not goods imported for local Djibouti sale.</p>
            </div>
          </div>

          <div className="p-5 rounded-sm border border-white/10 bg-white/5" data-testid="supplier-message-card">
            <h3 className="font-serif font-semibold text-lg mb-3">Message To Chinese Supplier</h3>
            <p className="text-xs text-white/55 whitespace-pre-line max-h-64 overflow-y-auto mb-4" data-testid="supplier-message-text">
              {SUPPLIER_MESSAGE}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={copySupplierMessage} className="trade-btn-primary rounded-sm h-10 px-4" data-testid="copy-supplier-message">
                <Copy size={14} className="mr-2" /> Copy Message
              </Button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(SUPPLIER_MESSAGE)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 text-sm"
                data-testid="whatsapp-supplier-message"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ShippingProcessModal;
